import React, { useMemo } from 'react';
import { Lock, LockOpen, Minus, Plus, RotateCcw, SkipForward, Trophy, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoringController } from '../../../hooks/useScoring';
import { BASE_POINTS, getStandings, roundValues, SCORE_LEVEL_LABELS, SCORE_LEVELS } from '../../../utils/scoring';
import { formatPoints } from './VerdictButtons';

interface ScoringPanelProps {
  scoring: ScoringController;
  /** Players to list even before they score. */
  roster?: string[];
  /**
   * Called after the moderator moves to another question (skip, pick a value,
   * change the level) so a host screen can clear its buzzer.
   */
  onQuestionChange?: () => void;
  className?: string;
}

const panelStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '1rem',
};

const ghostBtn =
  'inline-flex shrink-0 items-center whitespace-nowrap justify-center gap-1.5 rounded-[0.625rem] border border-white/10 bg-white/5 text-white/60 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-30';

const pillBtn = (active: boolean) =>
  cn(
    'rounded-[0.625rem] px-3 py-1.5 text-sm font-bold transition sm:px-4 sm:py-2',
    active
      ? 'bg-sky-500 text-white shadow-[0_0_14px_rgba(14,165,233,0.45)]'
      : 'text-slate-400 hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-400'
  );

/**
 * Standalone point counter: level / question controls and standings. It knows
 * nothing about the buzzer — a host screen renders it next to the presses and
 * wires `onQuestionChange` to its resets.
 */
const ScoringPanel: React.FC<ScoringPanelProps> = ({ scoring, roster = [], onQuestionChange, className }) => {
  const { state, canUndo } = scoring;
  const locked = state.levelLocked;
  const standings = useMemo(() => getStandings(state, roster), [state, roster]);
  const values = roundValues(state.level);
  const step = BASE_POINTS * state.level;

  const changeQuestion = (action: () => void) => {
    action();
    onQuestionChange?.();
  };

  return (
    <div style={panelStyle} className={cn('overflow-hidden', className)}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-white/5 px-4 py-3.5 sm:px-5">
        <h3 className="flex items-center gap-2 font-bold text-amber-300">
          <Trophy className="h-5 w-5" />
          Xal sayğacı
        </h3>
        {/* Icon-only on phones so the header stays on one line. */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={scoring.undo}
            disabled={!canUndo}
            title="Son əməliyyatı geri al"
            aria-label="Geri al"
            className={cn(ghostBtn, 'h-9 w-9 text-sm font-semibold sm:w-auto sm:px-3')}
          >
            <Undo2 className="h-4 w-4" />
            <span className="hidden sm:inline">Geri al</span>
          </button>
          <button
            type="button"
            onClick={scoring.resetScores}
            disabled={standings.every((p) => p.points === 0) && !state.lastEntry}
            title="Bütün xalları sıfırla — səviyyə və sual dəyişmir («Geri al» ilə qaytarmaq olar)"
            aria-label="Xalları sıfırla"
            className={cn(ghostBtn, 'h-9 w-9 text-sm font-semibold sm:w-auto sm:px-3')}
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden sm:inline">Xalları sıfırla</span>
          </button>
        </div>
      </div>

      <div className="grid gap-5 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* Controls */}
        <div className="flex flex-col gap-4">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-sky-300/60">Səviyyə</p>
            <div className="flex items-center gap-2">
              <div className="flex flex-wrap gap-1 rounded-[0.875rem] border border-white/10 bg-white/[0.04] p-1">
                {SCORE_LEVELS.map((level) => {
                  const active = state.level === level;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => changeQuestion(() => scoring.setLevel(level))}
                      disabled={locked && !active}
                      aria-pressed={active}
                      title={locked && !active ? 'Səviyyə kilidlidir' : SCORE_LEVEL_LABELS[level]}
                      aria-label={`${SCORE_LEVEL_LABELS[level]} ×${level}`}
                      className={pillBtn(active)}
                    >
                      {/* Phones show only the multiplier so all four fit on one row. */}
                      <span className="hidden sm:inline">{SCORE_LEVEL_LABELS[level]} </span>
                      <span className="sm:opacity-60">×{level}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => scoring.setLevelLocked(!locked)}
                aria-pressed={locked}
                title={locked ? 'Səviyyəni dəyişmək üçün kilidi aç' : 'Səviyyəni kilidlə'}
                aria-label={locked ? 'Səviyyəni dəyişmək üçün kilidi aç' : 'Səviyyəni kilidlə'}
                className={cn(
                  ghostBtn,
                  'h-11 w-11 shrink-0',
                  locked && 'border-amber-400/30 bg-amber-400/10 text-amber-300 hover:text-amber-200'
                )}
              >
                {locked ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-sky-300/60">Sual</p>
            <div className="flex flex-wrap items-center gap-2">
              {values.map((v, i) => {
                const active = state.questionNo === i + 1;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => changeQuestion(() => scoring.setQuestion(i + 1))}
                    aria-pressed={active}
                    className={cn(
                      'h-9 min-w-[2.5rem] rounded-[0.625rem] border px-2 text-sm font-black tabular-nums transition sm:h-11 sm:min-w-[3.5rem] sm:px-3 sm:text-base',
                      active
                        ? 'border-amber-400/70 bg-amber-400/20 text-amber-200'
                        : i + 1 < state.questionNo
                          ? 'border-white/5 bg-white/[0.02] text-white/30 hover:text-white/70'
                          : 'border-white/10 bg-white/5 text-white/70 hover:text-white'
                    )}
                  >
                    {v}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => changeQuestion(scoring.skipQuestion)}
                title="Heç kim düz cavab vermədi — növbəti suala keç"
                aria-label="Sualı keç"
                className={cn(ghostBtn, 'ml-auto h-9 w-9 text-sm font-semibold sm:h-11 sm:w-auto sm:px-3')}
              >
                <SkipForward className="h-4 w-4" />
                <span className="hidden sm:inline">Sualı keç</span>
              </button>
            </div>
          </div>

          {state.lastEntry && (
            <p className="text-sm text-white/50">
              Son: <span className="font-semibold text-white/80">{state.lastEntry.playerName}</span>{' '}
              <span className={state.lastEntry.points > 0 ? 'text-emerald-300' : 'text-rose-300'}>
                {formatPoints(state.lastEntry.points)}
              </span>
            </p>
          )}
        </div>

        {/* Standings */}
        <div className="flex flex-col gap-2">
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-300/60">Xallar</p>
          {standings.length === 0 ? (
            <p className="py-6 text-center text-sm text-white/30">Oyunçular qoşulduqca burada görünəcək.</p>
          ) : (
            standings.map((p, index) => (
              <div
                key={p.name}
                className={cn(
                  'flex items-center gap-3 rounded-xl border px-4 py-2.5',
                  index === 0 && p.points > 0 ? 'border-amber-400/40 bg-amber-400/10' : 'border-white/10 bg-white/5'
                )}
              >
                <span className="w-5 text-sm font-black tabular-nums text-white/40">{index + 1}</span>
                <span className="min-w-0 flex-1 truncate font-semibold text-white">{p.name}</span>
                <span
                  className={cn(
                    'text-xl font-black tabular-nums',
                    p.points > 0 ? 'text-emerald-300' : p.points < 0 ? 'text-rose-300' : 'text-white/50'
                  )}
                >
                  {p.points}
                </span>
                <div className="flex items-center gap-1" title={`Düzəliş: ±${step}`}>
                  <button
                    type="button"
                    onClick={() => scoring.adjust(p.name, -step)}
                    aria-label={`${p.name}: −${step}`}
                    className={cn(ghostBtn, 'h-8 w-8')}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => scoring.adjust(p.name, step)}
                    aria-label={`${p.name}: +${step}`}
                    className={cn(ghostBtn, 'h-8 w-8')}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoringPanel;
